import type { FormInstance } from 'antd/lib/form';

import { captureException } from '@sentry/react';
import { BASEURL, ENDPOINTS, SPXAPI, serverSignUp } from '@/api';
import type {
    CognitoAuthError,
    OTPAuthType,
    PhoneIdentity,
} from '@/types/types';
import { CognitoErrorCodes } from '@/types/types';
import { StatusMessage } from '../../Common/CodeAuthenticator';
import {
    captureErrorForSentry,
    retryRequest,
    checkIsNetworkError,
    authErrorMessageToI18nPayload,
    getErrorResponse,
    checkIsSpecificCode,
    caseInsensitiveUrlParam,
    tryGetTimelyRedirectPath,
} from '../../utils';
import { codeAuthNameSpace, COGNITO_CALL_RETRIES, LAMBDA_TIMEOUT_MESSAGE_PARTIAL } from './constants';

const generateRandomPass = (): string => (
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    `${Math.random().toString(36).slice(2)}${Math.random().toString(36).toUpperCase().slice(2)}!`
);

export const getLinkIdFromLocation = (search?: string): string => (
    caseInsensitiveUrlParam(new URLSearchParams(search ?? window.location.search), 'linkid') ?? ''
);

/** Resolve share linkId from the current URL or a saved post-login redirect path. */
export const getLinkIdForSignup = (): string => {
    const fromLocation = getLinkIdFromLocation();
    if (fromLocation) {
        return fromLocation;
    }
    const redirectPath = tryGetTimelyRedirectPath();
    if (!redirectPath) {
        return '';
    }
    const queryIndex = redirectPath.indexOf('?');
    if (queryIndex === -1) {
        return '';
    }
    return caseInsensitiveUrlParam(new URLSearchParams(redirectPath.slice(queryIndex + 1)), 'linkid') ?? '';
};

export interface TrySignUpOptions {
    linkId?: string;
}

export const generateCode = async (
    APIInstance: SPXAPI, emailLower: string, authType: OTPAuthType, phoneIdentity?: PhoneIdentity,
): Promise<void> => {
    const payload = phoneIdentity ? { body: phoneIdentity } : {};
    const linkId = getLinkIdFromLocation();
    await retryRequest<void>(
        async (): Promise<void> => (
            APIInstance.post(BASEURL.backend(), ENDPOINTS.generateCode(emailLower, authType, linkId), payload)
        ),
        (error) => {
            captureException(error);
            return checkIsNetworkError(error);
        },
        COGNITO_CALL_RETRIES,
    );
};

export const filterCognitoError = (error: CognitoAuthError): boolean => {
    let result = error.code === CognitoErrorCodes.NetworkError;
    if (!result) {
        result = (
            error.code === CognitoErrorCodes.UnexpectedLambdaException
            && error.message?.toLowerCase().includes(LAMBDA_TIMEOUT_MESSAGE_PARTIAL)
        );
    }
    return result;
};

// Narrower retry filter for `Auth.signIn` during the OTP flow.
// We deliberately do NOT retry on Cognito lambda socket-timeout here, because
// the underlying `create_auth_challenge` lambda may have already successfully
// dispatched the OTP email/SMS via Courier and only blown past Cognito's 5s
// invocation ceiling. Retrying then re-invokes `create_auth_challenge` while
// the first invocation is still mid-flight — `recent_otp.sent` has not been
// persisted yet, so the second invocation also calls `send_code_to_user`,
// producing the duplicate email reported by recipients.
// Genuine network failures still retry.
export const filterCognitoSignInRetryError = (error: CognitoAuthError): boolean => (
    error.code === CognitoErrorCodes.NetworkError
);

export class InvalidLinkError extends Error {
    constructor() {
        super('Invalid link');
        this.name = 'InvalidLinkError';
    }
}

export const trySignUp = async (
    usernameLower: string,
    APIInstance: SPXAPI,
    options: TrySignUpOptions = {},
): Promise<void> => {
    const linkId = options.linkId ?? getLinkIdForSignup();
    try {
        await retryRequest(
            async () => serverSignUp(APIInstance, {
                email: usernameLower,
                password: generateRandomPass(),
                clientMetadata: { source: 'otp-recipient' },
                ...(linkId ? { linkId } : {}),
            }),
            (error: unknown): boolean => checkIsNetworkError(error),
            COGNITO_CALL_RETRIES,
        );
    } catch (error) {
        if (checkIsSpecificCode(error, 403)) {
            throw new InvalidLinkError();
        }
        const isExistingUser = checkIsSpecificCode(error, 409)
            || (error as CognitoAuthError).code === CognitoErrorCodes.UsernameExistsException
            || (error as CognitoAuthError).message?.toLowerCase()?.includes('user with this email already exists');
        if (!isExistingUser) {
            console.log('sign up fail', error);
            throw error;
        }
    }
};

export const getEmail = (formInstance: FormInstance): string => {
    const { getFieldValue } = formInstance;
    return getFieldValue('email').toLowerCase();
};

const checkIsUserNotFound = (error: unknown): boolean => (
    (error as CognitoAuthError).code === CognitoErrorCodes.UserNotFoundException
);

type ErrorMessagePayload = Omit<StatusMessage, 'hasError'>;

const createErrorState = (error: unknown, isNotFound: boolean,
    isSignUpForbidden: boolean = false): ErrorMessagePayload => {
    const errorMessage = getErrorResponse(error)?.message || (error as CognitoAuthError)?.message;
    let i18nKey = '';
    if (isNotFound) {
        i18nKey = 'userNotFound';
    } else if (isSignUpForbidden) {
        i18nKey = 'signUpForbidden';
    } else {
        i18nKey = 'couldNotSendCode';
    }
    const [message, options] = authErrorMessageToI18nPayload(errorMessage, () => [
        `${codeAuthNameSpace}.firstStepCommon.${i18nKey}`,
    ]);
    return { message, options };
};

const checkIsSignUpForbidden = (error: unknown): boolean => {
    const errorResponse = getErrorResponse(error);

    return errorResponse.statusCode === 403 && errorResponse.message.includes('User is not registered yet.');
};

export const onSendCodeError = (error: unknown, setErrorMessage: (messages: StatusMessage[]) => void): void => {
    const isNotFound = checkIsUserNotFound(error);
    const isSignUpForbidden = checkIsSignUpForbidden(error);
    setErrorMessage([{
        ...createErrorState(error, isNotFound, isSignUpForbidden),
        hasError: true,
    }]);
    captureErrorForSentry(error, 'useOTPFlow.onSendCodeError', { severityLevel: isNotFound ? 'warning' : 'error' });
};

export const getPhoneValues = (formInstance: FormInstance): PhoneIdentity => {
    const { getFieldValue } = formInstance;
    return {
        prefix: getFieldValue('prefix').prefix,
        phone: getFieldValue('phone'),
    };
};
